import api from './api';

export interface CuentaCorrienteProveedorSaldo {
  nroProveedor: number;
  nombre: string;
  telefono?: string | null;
  saldoTotal: number;
  saldoVencido: number;
  saldoVigente: number;
  ultimaActualizacion: string;
}

export interface CuentaCorrienteProveedorComprobante {
  compania: string;
  nroProveedor: string;
  nombre: string;
  fecha: string;
  comprobante: string;
  numero: string;
  importe: number;
  saldoDeb: number;
  saldoHab: number;
  fechaVenc: string;
  saldo: number;
  nroTipoFormulario: string;
  moneda: number;
  /** Resuelto por la API (vista o join con Formularios); puede ser null si no se pudo enlazar */
  nroFormulario?: number | null;
}

export interface EstadisticasCuentaCorrienteProveedores {
  totalProveedoresConCuentaCorriente: number;
  saldoTotalGeneral: number;
  saldoVencidoTotal: number;
  totalMovimientos: number;
  ultimaActualizacion: string;
}

/** Listado deuda por vencimiento (GET informes/deuda-por-vencimiento) */
export interface ProveedorDeudaPorVencimiento {
  nroProveedor: number;
  nombre: string;
  codigoRuta?: number | null;
  nombreRuta?: string | null;
  codigoRamoProv: string;
  nombreRamoProv?: string | null;
  deudaHastaVencimiento: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

/** Alineado con CuentaCorrienteHistProveedorDto (vista BP_RPT_CuentasAPagar) */
export interface MovimientoHistoricoProveedor {
  nroProveedor: number | null;
  nombrePro: string | null;
  fechaEmision: string | null;
  fechaVencim: string | null;
  descripcionFor: string | null;
  descripcionTip: string | null;
  letra: string | null;
  nroSuc: number;
  nroComprobante: number | null;
  numero: string | null;
  importeTotal: number | null;
  importeCancelado: number | null;
  impCompDebe: number | null;
  impCompHaber: number | null;
  saldoComp: number | null;
  saldoCompDebe: number | null;
  saldoCompHaber: number | null;
  nroTipoFormulario: string | null;
  nroDoc: string | null;
  nroFormulario?: number | null;
  /** True si Formularios.Asentado = 'A' */
  anulado?: boolean;
}

export interface MovimientosHistoricosProveedorResponse extends PaginatedResponse<MovimientoHistoricoProveedor> {
  saldoInicial?: number | null;
}

export type SaldosProveedorSortBy =
  | 'nroProveedor'
  | 'nombre'
  | 'telefono'
  | 'saldoTotal'
  | 'saldoVigente'
  | 'saldoVencido';

export type SaldosProveedorSortDir = 'asc' | 'desc';

interface GetSaldosParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: SaldosProveedorSortBy;
  sortDir?: SaldosProveedorSortDir;
  soloConSaldo?: boolean;
  soloConSaldoVencido?: boolean;
}

interface GetComprobantesParams {
  pageNumber?: number;
  pageSize?: number;
}

interface GetMovimientosHistoricosParams {
  fechaDesde?: string;
  fechaHasta?: string;
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface GetDeudaPorVencimientoParams {
  fechaHasta: string;
  codigoRuta?: number;
  codigoRamoProv?: string;
}

const parseResponse = <T>(data: unknown): T => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as T;
    } catch (error) {
      console.error('No se pudo parsear la respuesta de Cuenta Corriente Proveedores:', error);
      throw new Error('Respuesta inválida del servidor de Cuenta Corriente Proveedores');
    }
  }

  return data as T;
};

const getSaldos = async ({
  pageNumber = 1,
  pageSize = 20,
  searchTerm,
  sortBy,
  sortDir,
  soloConSaldo,
  soloConSaldoVencido
}: GetSaldosParams = {}): Promise<PaginatedResponse<CuentaCorrienteProveedorSaldo>> => {
  const params: Record<string, string | number | boolean> = {
    pageNumber,
    pageSize
  };

  if (searchTerm) {
    params.searchTerm = searchTerm;
  }
  if (sortBy) {
    params.sortBy = sortBy;
  }
  if (sortDir) {
    params.sortDir = sortDir;
  }
  if (soloConSaldo) {
    params.soloConSaldo = true;
  }
  if (soloConSaldoVencido) {
    params.soloConSaldoVencido = true;
  }

  const response = await api.get('/api/CuentaCorrienteProveedores/saldos', {
    params,
    headers: {
      Accept: 'text/plain'
    }
  });

  return parseResponse<PaginatedResponse<CuentaCorrienteProveedorSaldo>>(response.data);
};

const getComprobantesProveedor = async (
  nroProveedor: number,
  {
    pageNumber = 1,
    pageSize = 50
  }: GetComprobantesParams = {}
): Promise<PaginatedResponse<CuentaCorrienteProveedorComprobante>> => {
  const response = await api.get(`/api/CuentaCorrienteProveedores/proveedor/${nroProveedor}`, {
    params: {
      pageNumber,
      pageSize
    },
    headers: {
      Accept: 'text/plain'
    }
  });

  return parseResponse<PaginatedResponse<CuentaCorrienteProveedorComprobante>>(response.data);
};

const getMovimientosHistoricosProveedor = async (
  nroProveedor: number,
  {
    fechaDesde,
    fechaHasta,
    searchTerm,
    pageNumber = 1,
    pageSize = 10
  }: GetMovimientosHistoricosParams = {}
): Promise<MovimientosHistoricosProveedorResponse> => {
  const params: Record<string, string | number | undefined> = {
    pageNumber,
    pageSize
  };
  if (fechaDesde) params.fechaDesde = fechaDesde;
  if (fechaHasta) params.fechaHasta = fechaHasta;
  if (searchTerm) params.searchTerm = searchTerm;

  const response = await api.get(
    `/api/CuentaCorrienteProveedores/proveedor/${nroProveedor}/movimientos-historicos`,
    {
      params,
      headers: { Accept: 'text/plain' }
    }
  );

  return parseResponse<MovimientosHistoricosProveedorResponse>(response.data);
};

const getEstadisticas = async (): Promise<EstadisticasCuentaCorrienteProveedores> => {
  const response = await api.get<EstadisticasCuentaCorrienteProveedores>('/api/CuentaCorrienteProveedores/estadisticas');
  return response.data;
};

const getDeudaPorVencimiento = async (
  params: GetDeudaPorVencimientoParams
): Promise<ProveedorDeudaPorVencimiento[]> => {
  const query: Record<string, string | number> = {
    fechaHasta: params.fechaHasta
  };
  if (params.codigoRuta != null && params.codigoRuta !== undefined) {
    query.codigoRuta = params.codigoRuta;
  }
  if (params.codigoRamoProv?.trim()) {
    query.codigoRamoProv = params.codigoRamoProv.trim();
  }

  const response = await api.get('/api/CuentaCorrienteProveedores/informes/deuda-por-vencimiento', {
    params: query,
    headers: { Accept: 'text/plain' }
  });

  return parseResponse<ProveedorDeudaPorVencimiento[]>(response.data);
};

const cuentaCorrienteProveedoresService = {
  getSaldos,
  getComprobantesProveedor,
  getMovimientosHistoricosProveedor,
  getEstadisticas,
  getDeudaPorVencimiento
};

export default cuentaCorrienteProveedoresService;

